import {Component, OnDestroy, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {MatSnackBar} from '@angular/material/snack-bar';
import {
  Field,
  FieldType,
  MemberAddeditComponent,
  MemberAddeditDialogModel,
} from '../../../brokeragency/member-addedit/member-addedit.component';
import {RewardsAdminService} from '../../../../services/rewards-admin/rewards-admin.service';
import {UiAlertService} from '../../../../services/ui-alert/ui-alert.service';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

@Component({
    selector: 'app-superadmin-rewards-brands',
    templateUrl: './superadmin-rewards-brands.component.html',
    styleUrls: ['./superadmin-rewards-brands.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class SuperadminRewardsBrandsComponent implements OnInit, OnDestroy {
  public displayedColumns: string[] = ['name', 'accountId', 'actions'];
  public items: any[] = [];
  public searchValue = '';
  public page = 1;
  public limit = 30;
  public endOfList = false;
  public loading = false;
  private readonly searchDebounceMs = 300;
  private readonly searchValueChanges = new Subject<string>();
  private readonly searchSubscription: Subscription;
  private readonly addEditFields: Array<Field> = [
    {name: 'name', label: 'Brand Name', fieldType: FieldType.F_STRING, required: true},
    {name: 'accountId', label: 'Account ID', fieldType: FieldType.F_STRING, required: false},
    {name: 'config', label: 'Config JSON', fieldType: FieldType.F_JSON, required: false},
    {name: 'partnerFilterMode', label: 'Partner Filter Mode JSON', fieldType: FieldType.F_JSON, required: false},
  ];

  constructor(
    private rewardsAdminService: RewardsAdminService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private uiAlertService: UiAlertService,
  ) {
    this.searchSubscription = this.searchValueChanges
      .pipe(
        debounceTime(this.searchDebounceMs),
        distinctUntilChanged(),
      )
      .subscribe(() => this.loadItems(true));
  }

  ngOnInit(): void {
    this.loadItems(true);
  }

  ngOnDestroy(): void {
    this.searchSubscription.unsubscribe();
  }

  async loadItems(clear = false): Promise<void> {
    if (clear) {
      this.page = 1;
      this.endOfList = false;
      this.items = [];
    }
    if (this.loading || this.endOfList) {
      return;
    }
    this.loading = true;
    try {
      const brands = await this.rewardsAdminService.getBrands(this.searchValue, this.page, this.limit);
      if (!brands || brands.length === 0) {
        this.endOfList = true;
      } else {
        this.items = this.items.concat((brands || []).map((brand: any) => this.normalizeBrandRecord(brand)));
        this.page += 1;
      }
    } catch (error) {
      console.error('Error loading brands', error);
    } finally {
      this.loading = false;
    }
  }

  queueFilter(value: string): void {
    this.searchValueChanges.next((value || '').toString());
  }

  addBrand(): void {
    const dialogRef = this.dialog.open(MemberAddeditComponent, {
      data: new MemberAddeditDialogModel(null, this.getDialogFields(), 'Brand', true, 'Add'),
    });
    dialogRef.afterClosed().subscribe((dialogResult) => {
      if (!dialogResult) {
        return;
      }

      const payload = this.buildBrandPayload(dialogResult);
      if (!payload) {
        return;
      }
      this.rewardsAdminService.postBrand(payload).then(() => {
        this.loadItems(true);
        this.snackBar.open('Brand successfully created', 'Close', {duration: 2000});
      }).catch((error) => {
        console.error('Error creating brand', error);
        this.snackBar.open('Unable to create brand', 'Close', {duration: 3000});
      });
    });
  }

  async editBrand(brand: any): Promise<void> {
    const brandId = this.getBrandId(brand);
    if (!brandId) {
      return;
    }

    try {
      const fullBrand = this.normalizeBrandRecord(await this.rewardsAdminService.getBrandById(brandId));
      const dialogRef = this.dialog.open(MemberAddeditComponent, {
        data: new MemberAddeditDialogModel(this.toDialogBrandData(fullBrand), this.getDialogFields(), 'Brand'),
      });
      dialogRef.afterClosed().subscribe((dialogResult) => {
        if (!dialogResult) {
          return;
        }

        const payload = this.buildBrandPayload(dialogResult, fullBrand);
        if (!payload) {
          return;
        }
        this.rewardsAdminService.putBrand(payload).then(() => {
          this.loadItems(true);
          this.snackBar.open('Brand successfully updated', 'Close', {duration: 2000});
        }).catch((error) => {
          console.error('Error updating brand', error);
          this.snackBar.open('Unable to update brand', 'Close', {duration: 3000});
        });
      });
    } catch (error) {
      console.error('Error loading brand', error);
      this.snackBar.open('Unable to load brand details', 'Close', {duration: 3000});
    }
  }

  async deleteBrand(event: Event, brand: any): Promise<void> {
    event.stopPropagation();
    const brandId = this.getBrandId(brand);
    if (!brandId) {
      return;
    }

    const confirm = await this.uiAlertService.presentAlertConfirm('Delete this brand?');
    if (!confirm) {
      return;
    }

    try {
      await this.rewardsAdminService.deleteBrand({id: brandId});
      this.loadItems(true);
      this.snackBar.open('Brand successfully deleted', 'Close', {duration: 2000});
    } catch (error) {
      console.error('Error deleting brand', error);
      this.snackBar.open('Unable to delete brand', 'Close', {duration: 3000});
    }
  }

  onScroll(event: any): void {
    const scrollTop = event.target.scrollTop;
    const scrollHeight = event.target.scrollHeight;
    const offsetHeight = event.target.offsetHeight;
    const threshold = 150;
    if (scrollTop + offsetHeight >= scrollHeight - threshold) {
      this.loadItems();
    }
  }

  private getDialogFields(): Array<Field> {
    return this.addEditFields.map((field) => ({...field}));
  }

  private toDialogBrandData(brand: any): Record<string, any> {
    return {
      id: this.getBrandId(brand),
      name: brand?.name || '',
      accountId: brand?.accountId || '',
      config: this.parseJsonValue(brand?.config),
      partnerFilterMode: this.parseJsonValue(brand?.partnerFilterMode),
    };
  }

  private buildBrandPayload(dialogResult: Record<string, any>, currentBrand?: any): Record<string, any> | null {
    const name = String(dialogResult?.name || '').trim();
    if (!name) {
      this.snackBar.open('Brand name is required', 'Close', {duration: 3000});
      return null;
    }

    const payload: Record<string, any> = {
      name,
      accountId: dialogResult?.accountId || '',
      config: this.parseJsonValue(dialogResult?.config),
      partnerFilterMode: this.parseJsonValue(dialogResult?.partnerFilterMode),
    };

    const brandId = this.getBrandId(currentBrand);
    if (brandId) {
      payload.id = brandId;
    }

    return payload;
  }

  private getBrandId(brand: any): string {
    return String(brand?.id || brand?.brandId || brand?._id || '').trim();
  }

  private normalizeBrandRecord(brand: any): any {
    if (!brand) {
      return brand;
    }

    const brandId = this.getBrandId(brand);
    return brandId ? {...brand, id: brandId} : brand;
  }

  private parseJsonValue(value: any): any {
    if (typeof value === 'string') {
      try {
        return value ? JSON.parse(value) : {};
      } catch {
        return value;
      }
    }
    return value ?? {};
  }
}
